import { DateTime } from "luxon"
import {
    Box,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { selectSelectedLocale } from "../../../../../../modules/localization/localizationSelectors"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { FormikDatePicker, FormikTextField } from "../../../../forms/Formik"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { StandardButton } from "../../../../buttons/StandardButton"
import { RepeatOrderFormValues } from "./RepeatOrderDialog"
import { DeliveryTimesField } from "../../form/formTypes"

const useStyles = makeStyles()((theme) => ({
    numberField: {
        width: "4rem",
    },
    radioGroup: {
        gap: ".5rem",
    },
    helperTextDisabled: {
        color: theme.palette.tailwind.slate[500],
    },
    helperText: {
        color: theme.palette.tailwind.slate[800],
    },
    toggleButtonGroup: {
        gap: ".5rem",
    },
    toggleButton: {
        textAlign: "center",
        fontWeight: "400",
        textTransform: "capitalize",
        width: "2.5rem",
        height: "2.5rem",
        padding: "0",
        color: theme.palette.tailwind.slate[800],
        backgroundColor: theme.palette.common.white,
        borderColor: theme.palette.tailwind.slate[300],
        borderRadius: "50%",
        "&.Mui-selected": {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.tailwind.blue[500],
        },
        "&.MuiToggleButtonGroup-grouped:not(:first-of-type), &.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "50%",
            borderColor: theme.palette.tailwind.slate[300],
        },
    },
    buttonWrap: {
        textAlign: "center",
        paddingTop: "1.5rem",
    },
    button: {
        minWidth: "15rem",
    },
}))

const weekDays = Array.from({ length: 7 }, (_, i) =>
    DateTime.local()
        .set({ weekday: i + 1 })
        .toFormat("EEEE"),
)

type RepeatOrderDialogFormProps = Readonly<{
    deliveries: DeliveryTimesField
}>

export function RepeatOrderDialogForm({ deliveries }: RepeatOrderDialogFormProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const locale = useAppSelector(selectSelectedLocale)
    const today = dateTimeFactory.now().startOf("day")

    const { values, setFieldValue, errors } = useFormikContext<RepeatOrderFormValues>()

    const deliveryDate = deliveries.deliveryDate
        ? dateTimeFactory.fromISO(deliveries.deliveryDate).startOf("day")
        : today

    const minStartDate = deliveryDate < today ? today : deliveryDate

    const selectedStartDate = values.startDate ? values.startDate.startOf("day") : null
    const effectiveMinEndDate = selectedStartDate && selectedStartDate > minStartDate ? selectedStartDate : minStartDate

    return (
        <Stack gap="2.5rem">
            <Stack gap="1.5rem">
                <Stack gap=".5rem">
                    <LocalizedStrict id="create-order-drawer-repeat-order-every">
                        <Typography variant="subtitle1">Repeat every</Typography>
                    </LocalizedStrict>
                    <Stack flexDirection="row" gap="1rem" alignItems="center">
                        <FormikTextField
                            name="repeatInterval"
                            type="number"
                            inputProps={{ min: 1 }}
                            size="small"
                            className={classes.numberField}
                        />
                        <RadioGroup
                            row
                            value={values.repeatType}
                            onChange={(e) => setFieldValue("repeatType", e.target.value)}
                        >
                            <LocalizedStrict id="create-order-drawer-repeat-order-every-day" attrs={{ label: true }}>
                                <FormControlLabel value="day" control={<Radio size="small" />} label="Day" />
                            </LocalizedStrict>
                            <LocalizedStrict id="create-order-drawer-repeat-order-every-week" attrs={{ label: true }}>
                                <FormControlLabel value="week" control={<Radio size="small" />} label="Week" />
                            </LocalizedStrict>
                        </RadioGroup>
                    </Stack>
                </Stack>
                {values.repeatType === "week" && (
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-on">
                            <Typography variant="subtitle1">Repeat on</Typography>
                        </LocalizedStrict>
                        <ToggleButtonGroup
                            value={values.weekDays || []}
                            onChange={(_, newValue) => setFieldValue("weekDays", newValue)}
                            className={classes.toggleButtonGroup}
                        >
                            {weekDays.map((day) => (
                                <ToggleButton key={day} value={day} className={classes.toggleButton}>
                                    {DateTime.fromFormat(day, "EEEE")
                                        .setLocale(locale)
                                        .toLocaleString({ weekday: "short" })}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        {errors.weekDays && <FormHelperText error>{errors.weekDays}</FormHelperText>}
                    </Stack>
                )}
                <Stack flexDirection="row" gap="5.5rem" justifyContent="space-between">
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-starts">
                            <Typography variant="subtitle1">Starts</Typography>
                        </LocalizedStrict>
                        <FormikDatePicker
                            name="startDate"
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                            minDate={minStartDate}
                        />
                    </Stack>
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-ends">
                            <Typography variant="subtitle1">Ends</Typography>
                        </LocalizedStrict>
                        <RadioGroup
                            value={values.endType}
                            onChange={(e) => setFieldValue("endType", e.target.value)}
                            className={classes.radioGroup}
                        >
                            <Stack flexDirection="row" gap="1.5rem">
                                <LocalizedStrict id="create-order-drawer-repeat-order-ends-on" attrs={{ label: true }}>
                                    <FormControlLabel value="on" control={<Radio size="small" />} label="On" />
                                </LocalizedStrict>
                                <FormikDatePicker
                                    name="endDate"
                                    disabled={values.endType !== "on"}
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    minDate={effectiveMinEndDate}
                                />
                            </Stack>
                            <Stack flexDirection="row" gap="2.5rem">
                                <LocalizedStrict
                                    id="create-order-drawer-repeat-order-ends-after"
                                    attrs={{ label: true }}
                                >
                                    <FormControlLabel value="after" control={<Radio size="small" />} label="After" />
                                </LocalizedStrict>
                                <Stack flexDirection="row" alignItems="center" gap=".5rem">
                                    <FormikTextField
                                        type="number"
                                        name="repeatCount"
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 1 }}
                                        disabled={values.endType !== "after"}
                                    />
                                    <LocalizedStrict id="create-order-drawer-repeat-order-ends-after-times">
                                        <Typography
                                            className={cx(
                                                values.endType !== "after"
                                                    ? classes.helperTextDisabled
                                                    : classes.helperText,
                                            )}
                                        >
                                            times
                                        </Typography>
                                    </LocalizedStrict>
                                </Stack>
                            </Stack>
                        </RadioGroup>
                    </Stack>
                </Stack>
            </Stack>

            <Box className={classes.buttonWrap}>
                <StandardButton fullWidth={false} updateDefaultClasses={classes.button} type="submit">
                    Confirm
                </StandardButton>
            </Box>
        </Stack>
    )
}
